/**
 * Cookie consent.
 *
 * Drives every element marked with [data-cookie-consent]: the site-wide modal
 * and the inline preferences form on the cookie policy page share this logic.
 *
 * Talks to django-cookie-consent's accept/decline endpoints. Those views are
 * csrf_exempt and, when sent the X-Cookie-Consent-Fetch header, answer with an
 * empty 200 instead of a redirect.
 *
 * After a choice is saved the page is reloaded: whether a third-party script
 * (e.g. the Meta Pixel) ends up in the HTML is decided server-side, so the page
 * has to be re-rendered for it to start — or stop — loading.
 */
class ConsentForm {
  constructor(root) {
    this.root = root
    this.acceptUrlTemplate = root.dataset.acceptUrl
    this.declineUrlTemplate = root.dataset.declineUrl

    this.initTriggers()
  }

  initTriggers() {
    const on = (selector, handler) => {
      const element = this.root.querySelector(selector)
      if (element) element.addEventListener('click', handler)
    }

    on('[data-show-details]', () => this.root.classList.add('is-showing-details'))
    on('[data-accept-all]', () => this.submit(this.groupNames(), []))
    on('[data-decline-all]', () => this.submit([], this.groupNames()))
    on('[data-save-preferences]', () =>
      this.submit(
        this.groupNames((input) => input.checked),
        this.groupNames((input) => !input.checked)
      )
    )
  }

  groupNames(filter = () => true) {
    return Array.from(this.root.querySelectorAll('[data-cookie-group]'))
      .filter((input) => !input.disabled)
      .filter(filter)
      .map((input) => input.dataset.cookieGroup)
  }

  buildUrl(template, varnames) {
    return template.replace('__GROUPS__', varnames.join(','))
  }

  send(url) {
    return fetch(url, {
      method: 'POST',
      headers: { 'X-Cookie-Consent-Fetch': 'true' },
      credentials: 'same-origin',
    })
  }

  async submit(accepted, declined) {
    this.setBusy(true)

    try {
      // Sequential on purpose: each endpoint rebuilds the whole consent cookie
      // from the one sent with the request, so the second call must already see
      // the cookie set by the first.
      if (accepted.length) {
        await this.send(this.buildUrl(this.acceptUrlTemplate, accepted))
      }
      if (declined.length) {
        await this.send(this.buildUrl(this.declineUrlTemplate, declined))
      }
      window.location.reload()
    } catch (error) {
      console.error('Cookie consent: saving the choice failed', error)
      this.setBusy(false)
    }
  }

  setBusy(busy) {
    this.root
      .querySelectorAll('button')
      .forEach((button) => (button.disabled = busy))
  }
}

export default class CookieConsent {
  constructor(modals) {
    this.forms = Array.from(
      document.querySelectorAll('[data-cookie-consent]')
    ).map((root) => new ConsentForm(root))

    // Open the modal by itself only while some group is still undecided; the
    // template adds data-auto-open in that case.
    const modal = document.querySelector(
      '.modal[data-id="modal-cookie-consent"][data-auto-open]'
    )
    if (modal && modals) modals.open(modal)
  }
}
